/**
 * UITargetRegistry — Singleton registry for resolving named UI targets
 * (e.g. 'startButton', 'placementTile') to screen-space rectangles.
 *
 * Scenes register their UI elements at creation time; SpotlightRenderer
 * and CinematicDirector query bounds at runtime.
 */

export interface ScreenRect {
  x: number; y: number; w: number; h: number;
}

type BoundsResolver = () => ScreenRect | null;
type MultiBoundsResolver = () => ScreenRect[] | null;

const registry = new Map<string, BoundsResolver>();
const multiRegistry = new Map<string, MultiBoundsResolver>();

export const UITargetRegistry = {
  // ── Single-rect targets ──

  /** Register a named target with a bounds resolver function */
  register(key: string, resolver: BoundsResolver): void {
    registry.set(key, resolver);
  },

  /** Remove a registered target */
  unregister(key: string): void {
    registry.delete(key);
  },

  /** Get current screen-space bounds for a named target (null if unresolved) */
  getBounds(key: string): ScreenRect | null {
    const resolver = registry.get(key);
    if (!resolver) return null;
    return resolver();
  },

  /** Get all registered single-target keys */
  getKeys(): string[] {
    return Array.from(registry.keys());
  },

  // ── Multi-rect targets ��─

  /** Register a named multi-target with a resolver returning ScreenRect[] */
  registerMulti(key: string, resolver: MultiBoundsResolver): void {
    multiRegistry.set(key, resolver);
  },

  /** Remove a registered multi-target */
  unregisterMulti(key: string): void {
    multiRegistry.delete(key);
  },

  /** Get current screen-space bounds array for a multi-target (null if unresolved) */
  getMultiBounds(key: string): ScreenRect[] | null {
    const resolver = multiRegistry.get(key);
    if (!resolver) return null;
    return resolver();
  },

  /** Get all registered multi-target keys */
  getMultiKeys(): string[] {
    return Array.from(multiRegistry.keys());
  },

  /** Get all keys (single + multi) */
  getAllKeys(): string[] {
    return [...Array.from(registry.keys()), ...Array.from(multiRegistry.keys())];
  },
};
